///
/// EarthBrowser
///
/// Created by Matt Giger on December 14, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { isDevelopment } from '@/utils/environment'

export interface CommandRegistration {
  id: string
  title: string
  icon: React.ReactNode
  category?: string
  devOnly?: boolean
  enabled?: boolean
  onSelect: () => void
}

export interface BrowserFeature {
  id: string
  command: CommandRegistration
  renderBrowser: () => React.ReactNode
}

class CommandRegistryClass {
  private features = new Map<string, BrowserFeature>()
  private listeners = new Set<() => void>()

  /**
   * Register a browser feature with its command
   */
  registerFeature(feature: BrowserFeature): () => void {
    this.features.set(feature.id, feature)
    this.notifyListeners()

    // Return unregister function
    return () => {
      this.features.delete(feature.id)
      this.notifyListeners()
    }
  }

  /**
   * Get all registered commands, filtered by visibility
   */
  getCommands(): CommandRegistration[] {
    const includeDevCommands = isDevelopment()

    return Array.from(this.features.values())
      .map(feature => feature.command)
      .filter(command => {
        if (command.devOnly && !includeDevCommands) {
          return false
        }
        return true
      })
  }

  /**
   * Get all features for rendering
   */
  getFeatures(): BrowserFeature[] {
    return Array.from(this.features.values())
  }

  /**
   * Subscribe to registry changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener())
  }
}

// Singleton instance
export const CommandRegistry = new CommandRegistryClass()

